import { MRT_ColumnDef } from 'mantine-react-table';
import { Text } from '@mantine/core';
import type { CSSProperties } from 'react';
import { formatNumberToCurrency } from 'app/utils/util-functions';
import { SubTradeParams } from 'app/models/sub-trade-params';
import SellerPositionHoverCard from 'app/views/components/seller-position-hover-card/seller-position-hover-card';
import { facilityStatusInfo } from './trade-enty-page-columnDefs';

const getTextStyle = (color: string, weight = 600, textAlign?: 'start' | 'end' | 'center'): CSSProperties => ({
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '14px',
    fontWeight: weight,
    lineHeight: '19px',
    textAlign: textAlign ?? 'center',
    color,
});

function TableHead({ header, textAlign }: { header: string, textAlign?: 'start' | 'end' | 'center' }) {
    return (
        <Text style={getTextStyle('#784595', 400, textAlign)}>
            {header}
        </Text>
    );
}

function createEntityCell(columnType: 'myEntity' | 'counterparty') {
    return ({ row }: { row: any }) => {
        // only show hover card for selling entity - will be different column depending on whether buy or sell
        const entity = columnType === 'myEntity'
            ? (row.original.myEntity === 'Buy' ? row.original.buyerEntity : row.original.sellerEntity)
            : (row.original.myEntity === 'Sell' ? row.original.buyerEntity : row.original.sellerEntity);

        const entityName = entity?.entityName ?? 'Unknown';
        const entityMei = entity?.mei ?? 'N/A';

        const isShowingSeller = columnType === 'myEntity'
            ? row.original.myEntity === 'Sell'
            : row.original.myEntity === 'Buy';

        const sellerEntity = row.original.sellerEntity;

        const facilityIds = row.original.tradedFacilities?.map((tf: any) => tf.facilityId?.id).filter((id: any): id is string => !!id) || [];

        // only show seller position hover card if trades are draft or open status
        const subtradeStatus = (row.original.status ?? '').toLowerCase();
        const shouldShowHoverCard = isShowingSeller && (subtradeStatus === 'open' || subtradeStatus === 'draft');

        const nameElement = (
            <Text style={{
                ...getTextStyle('#476DB9'),
                textAlign: 'start'
            }}>
                {entityName}
            </Text>
        );

        return (
            <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {shouldShowHoverCard ? (
                        <SellerPositionHoverCard
                            sellerId={sellerEntity?.id || ''}
                            facilityIds={facilityIds}
                        >
                            {nameElement}
                        </SellerPositionHoverCard>
                    ) : (
                        nameElement
                    )}
                </div>
                <Text style={{ ...getTextStyle('#757373'), textAlign: 'start' }}>{entityMei}</Text>
            </div>
        );
    };
}

export const columnDefs: MRT_ColumnDef<SubTradeParams>[] = [
    {
        size: 50,
        accessorKey: 'status',
        header: 'Status',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: <div style={{ width: "100%" }}><TableHead header={'Status'} /></div>,
        Cell: ({ cell }) => {
            const status = (cell.getValue() as string ?? '').toLowerCase();
            const statusColors: { [key: string]: string } = {
                open: '#287A26',
                settled: '#EE5959',
                draft: '#757373',
                canceled: '#EE5959',
                terminated: '#E8006E',
            };

            return (
                <Text style={getTextStyle(statusColors[status] || '#000000')}>
                    {cell.getValue() as string ?? ''}
                </Text>
            );
        },
    },
    {
        size: 80,
        header: 'Sub Trade Ids',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'SubTrade ID'} textAlign='start' />
                <TableHead header={'Internal ID'} textAlign='start' />
            </div>
        ),
        Cell: ({ row }) => {
            const subTradeId = row.original.systemId ?? row.original.id;
            const clientId = row.original.clientId;
            return (
                <div style={{ width: '100%' }}>
                    <Text style={{ ...getTextStyle('#476DB9'), textAlign: 'start' }}>
                        {subTradeId}
                    </Text>
                    <Text style={{ ...getTextStyle('#757373'), textAlign: 'start' }}>
                        {clientId}
                    </Text>
                </div>
            );
        },
    },
    {
        size: 60,
        header: 'Settlement Date',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Settlement Date'} />
                <TableHead header={'T+'} />
            </div>
        ),
        Cell: ({ row }) => {
            const settlementDate = row.original.targetSettlementDate;
            const tPlus = row.original.tPlus;
            return (
                <div style={{ width: '100%' }}>
                    <Text style={getTextStyle('#476DB9')}>{settlementDate}</Text>
                    <Text style={getTextStyle('#757373')}>{tPlus}</Text>
                </div>
            );
        },
    },

    {
        size: 60,
        header: 'My Entity',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'My Entity'} textAlign='start' />
                <TableHead header={'MEI'} textAlign='start' />
            </div>
        ),
        Cell: createEntityCell('myEntity'),
    },
    {
        size: 60,
        header: 'Counterparty',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Counterparty'} textAlign='start' />
                <TableHead header={'MEI'} textAlign='start' />
            </div>
        ),
        Cell: createEntityCell('counterparty'),
    },
    {
        size: 80,
        header: 'Facilities',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Facilities'} textAlign='start' />
            </div>
        ),
        Cell: ({ row }) => {
            const tradedFacilities = row.original.tradedFacilities ?? [];

            return (
                <div>
                    {tradedFacilities.map((facility: any, index: number) => {
                        const facilityDetails = facility.facilityDetails;
                        return (
                            <Text key={index} style={{ ...getTextStyle('#476DB9'), textAlign: 'start' }}>
                                {(facilityDetails?.name ?? '') + ' (' + (facilityDetails?.cusip ?? 'N/A') + ') '}
                            </Text>
                        );
                    })}
                </div>
            );
        },
    },
    {
        size: 60,
        header: 'Facility Status',
        mantineTableHeadCellProps: { className: 'cell-width-100' },
        Header: () => <div style={{ width: '100%' }}><TableHead header={'Facility Status'} textAlign='start' /></div>,
        Cell: ({ row }) => {
            const tradedFacilities = row.original.tradedFacilities ?? [];
            return (
                <div>
                    {tradedFacilities.map((facility: any, index: number) => {
                        const info = facilityStatusInfo[facility.facilityDetails?.status]
                        return (
                            <Text
                                key={index}
                                style={{ ...getTextStyle(info?.color ?? '#413E3E'), textAlign: 'start' }}
                            >
                                {info?.display ?? ''}
                            </Text>
                        );
                    })}
                </div>
            );
        },
    },
    {
        size: 30,
        header: 'Traded Amount',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Traded Amount'} textAlign='end' /></div>
        ),
        Cell: ({ row }) => {
            const tradedFacilities = row.original.tradedFacilities ?? [];
            return (
                <div>
                    {tradedFacilities.map((facility: any, index: number) => {
                        const facilityDetails = facility.facilityDetails;
                        return (
                            <Text key={index} style={{ ...getTextStyle('#476DB9'), textAlign: 'end' }}>
                                {formatNumberToCurrency(Number(facility.tradeAmount), facilityDetails?.baseCurrency ?? 'USD')}
                            </Text>
                        );
                    })}
                </div>
            );
        },
    },
    {
        size: 30,
        header: 'Current Amount',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Current Amount'} textAlign='end' /></div>
        ),
        Cell: ({ row }) => {
            const tradedFacilities = row.original.tradedFacilities ?? [];
            return (
                <div>
                    {tradedFacilities.map((facility: any, index: number) => {
                        const facilityDetails = facility.facilityDetails;
                        const currentAmount = facility.amounts && facility.amounts.length > 0
                            ? row.original.lockedDate
                                ? facility.amounts?.findLast((amountWithDate: any) => new Date(amountWithDate.effectiveDate) <= new Date(row.original.lockedDate ?? ''))?.amount
                                : facility.amounts[facility.amounts.length - 1].amount
                            : '0';

                        return (
                            <Text key={index} style={{ ...getTextStyle('#476DB9'), textAlign: 'end' }}>
                                {formatNumberToCurrency(Number(currentAmount), facilityDetails?.baseCurrency ?? 'USD')}
                            </Text>
                        );
                    })}
                </div>
            );
        },
    },
    {
        size: 30,
        header: 'Price',
        mantineTableHeadCellProps: {
            className: 'cell-width-100',
        },
        Header: () => (
            <div style={{ width: '100%' }}>
                <TableHead header={'Price'} textAlign='end' /></div>
        ),
        Cell: ({ row }) => {
            const tradedFacilities = row.original.tradedFacilities ?? [];
            return (
                <div>
                    {tradedFacilities.map((facility: any, index: number) => {
                        return (
                            <Text key={index} style={{ ...getTextStyle('#476DB9'), textAlign: 'end' }}>
                                {facility.price}
                            </Text>
                        );
                    })}
                </div>
            );
        },
    }
];
